'use client';

import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import { useTranslation } from 'react-i18next';
import { Menu, X, MapPin, Phone, Mail, Facebook, Linkedin } from 'lucide-react';
import { LanguageSwitcher } from '@/components/ui/LanguageSwitcher';
import { FloatingContacts } from '@/components/ui/FloatingContacts';
import { CookieConsent } from '@/components/ui/CookieConsent';

export const UserLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { t } = useTranslation();
    const pathname = usePathname();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    useEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    }, [pathname]);

    const navItems = [
        { path: '/', label: t('nav.home') },
        { path: '/knowledge-hub', label: t('nav.knowledgeHub') },
        { path: '/blog', label: t('nav.blog') },
        { path: '/team', label: t('nav.team') },
        { path: '/about', label: t('nav.about') },
    ];

    return (
        <div className="user-layout">
            <header className="header glass">
                <div className="container">
                    <div className="header-content">
                        <Link href="/" className="logo">
                            <Image src="/LOGO.png" alt="GE-MP Consulting" className="logo-image" width={220} height={64} priority />
                        </Link>

                        <nav className={`nav ${mobileMenuOpen ? 'nav-open' : ''}`}>
                            {navItems.map((item) => (
                                <Link
                                    key={item.path}
                                    href={item.path}
                                    className={`nav-link ${pathname === item.path ? 'nav-link-active' : ''}`}
                                    onClick={() => setMobileMenuOpen(false)}
                                >
                                    {item.label}
                                </Link>
                            ))}
                            <div className="nav-actions">
                                <LanguageSwitcher />
                            </div>
                        </nav>

                        <button
                            className="mobile-menu-btn"
                            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                            aria-label="Toggle menu"
                        >
                            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
                        </button>
                    </div>
                </div>
            </header>

            <main className="main-content">{children}</main>

            <footer className="footer">
                <div className="container">
                    <div className="footer-brand">
                        <Image src="/LOGO.png" alt="GEMP" className="footer-logo-image" width={220} height={64} loading="lazy" />
                        <div className="footer-brand-subtitle">{t('footer.brandSubtitle')}</div>
                        {/* <div className="footer-brand-prop">{t('footer.brandProp')}</div> */}
                    </div>

                    <div className="footer-grid">
                        <div className="footer-card">
                            <MapPin size={32} />
                            <h4>{t('footer.addressTitle')}</h4>
                            <p>{t('footer.addressText')}</p>
                        </div>
                                                <div className="footer-card">
                            <MapPin size={32} />
                            <h4>{t('footer.addressTitle_2')}</h4>
                            <p>{t('footer.addressText_2')}</p>
                        </div>                         
                        {/* <div className="footer-card">
                            <Phone size={32} />
                            <h4>{t('footer.landlineTitle')}</h4>
                            <p>{t('footer.landlineText')}</p>
                        </div> */}
                        <div className="footer-card">
                            <Mail size={32} />
                            <h4>{t('footer.mailTitle')}</h4>
                            <p>{t('footer.mailText')}</p>
                        </div>
                        <div className="footer-card">
                            <MapPin size={32} />
                            <h4>{t('footer.accraTitle')}</h4>
                            <p>{t('footer.accraText')}</p>
                        </div>
                    </div>

                    <div className="footer-socials">
                        {/* <a href="https://x.com" target="_blank" rel="noreferrer" aria-label="X">
                            <X size={18} />
                        </a> */}
                        {/* <a href="https://facebook.com" target="_blank" rel="noreferrer" aria-label="Facebook">
                            <Facebook size={18} />
                        </a> */}
                        <a href="http://linkedin.com/company/gemp-consulting-german-egyptian-manufacturing-procurement/" target="_blank" rel="noreferrer" aria-label="LinkedIn">
                            <Linkedin size={18} />
                        </a>
                    </div>

                    <div className="footer-bottom">
                        <div className="footer-legal">
                            <Link href="/privacy-policy">{t('footer.privacyPolicy')}</Link>
                            <Link href="/impressum">{t('footer.impressum')}</Link>
                            <Link href="/terms-of-service">{t('footer.termsOfService')}</Link>
                        </div>
                        <div className="footer-meta">
                            <p className="footer-copy">{t('footer.copyright')}</p>
                            <p className="footer-developed">
                                {t('footer.createdByPrefix')}{' '}
                                <a
                                    href="https://www.boldframesolutions.com"
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    {t('footer.createdByName')}
                                </a>
                                .
                            </p>
                        </div>
                    </div>
                </div>
            </footer>

            <FloatingContacts />
            <CookieConsent />
        </div>
    );
};
